import { t, Trans } from "@lingui/macro";
import { CircleNotch, Crown, FileJs, FilePdf, Shield, Export, Question, FileCode, ShieldCheck } from "@phosphor-icons/react";
import {
  Button,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
} from "@stylingcv/ui";
import { cn } from "@stylingcv/utils";
import { useQueryClient } from "@tanstack/react-query";
import { saveAs } from "file-saver";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { SignupModal } from "@/client/components/auth/signup-modal";
import { PdfLoadingModal } from "@/client/components/pdf-loading-modal";
import { PostDownloadShareModal } from "@/client/components/post-download-share-modal";
import { useGuestResume } from "@/client/hooks/use-guest-resume";
import { useTemplateAccess } from "@/client/hooks/use-template-access";
import { useToast } from "@/client/hooks/use-toast";
import { usePrintResume } from "@/client/services/resume/print";
import { useUser } from "@/client/services/user/user";
import { useAuthStore } from "@/client/stores/auth";
import { useResumeStore } from "@/client/stores/resume";
import { forceDownload } from "@/client/utils/download";
import { trackDownloadResume } from "@/client/utils/gtm";

const onJsonExport = () => {
  const { resume } = useResumeStore.getState();
  const filename = `stylingcv_resume-${resume.id}.json`;
  const resumeJSON = JSON.stringify(resume.data, null, 2);

  // Track JSON export
  trackDownloadResume({
    resumeId: resume.id,
    format: "json",
    templateName: resume.data.metadata.template,
  });

  saveAs(new Blob([resumeJSON], { type: "application/json" }), filename);
};

export const ExportSection = () => {
  const { printResume, loading } = usePrintResume();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { saveToLocalStorage, downloadAsJson } = useGuestResume();

  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [authModalType, setAuthModalType] = useState<"save" | "download">("download");
  const [showPdfLoadingModal, setShowPdfLoadingModal] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);

  const { user, loading: userLoading } = useUser();
  const subscriptionTier = useAuthStore((state) => state.subscriptionTier);
  const isLoggedIn = !!user;

  const setValue = useResumeStore((state) => state.setValue);
  const metadata = useResumeStore((state) => {
    try {
      return state.resume.data.metadata;
    } catch {
      return {};
    }
  });

  // Get verified badge visibility state (default: true)
  const showVerifiedBadge = (metadata as any)?.showVerifiedBadge ?? true;
  const isVerified = (metadata as any)?.verified ?? false;

  // Get resume ID and user data for the modal
  const id = useResumeStore((state) => state.resume.id);
  const title = useResumeStore((state) => state.resume.title);
  const userData = useResumeStore((state) => {
    try {
      return {
        name: state.resume.data.basics.name || "",
        email: state.resume.data.basics.email || "",
      };
    } catch {
      return {
        name: "",
        email: "",
      };
    }
  });

  // Get template access permissions
  const templateTheme = (metadata as any)?.theme;
  const { canDownloadPdf, isPremiumTemplate } = useTemplateAccess(templateTheme);
  const monthStartUtc = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), 1));
  const hasUsedMonthlyFreePdfExport =
    !!user &&
    user.subscriptionTier === "FREE" &&
    !isPremiumTemplate &&
    !!user.freePdfLastExportAt &&
    new Date(user.freePdfLastExportAt) >= monthStartUtc;

  const onPdfExport = async () => {
    const templateName = (metadata as { template?: string })?.template;

    if (!isLoggedIn) {
      trackDownloadResume({
        resumeId: id,
        format: "pdf",
        templateName,
        outcome: "redirect_to_signup",
      });
      setAuthModalType("download");
      setShowAuthModal(true);
      return;
    }

    // Check template access permissions (this replaces the old FREE tier check)
    if (!canDownloadPdf) {
      trackDownloadResume({
        resumeId: id,
        format: "pdf",
        templateName,
        outcome: "redirect_to_pricing",
      });

      if (hasUsedMonthlyFreePdfExport) {
        toast({
          variant: "error",
          title: t`Monthly free PDF already used`,
          description: t`You have already used your free PDF export this month.`,
        });
      }

      // Navigate to the pricing page
      navigate("/pricing");
      return;
    }

    // Show loading modal immediately
    setShowPdfLoadingModal(true);

    // Use setTimeout to ensure state update and re-render happens
    void setTimeout(async () => {
      try {
        // Get the current resume data from the store
        const { resume } = useResumeStore.getState();
        const currentResumeData = resume.data;

        // Ensure minimum display time for loading modal
        const startTime = Date.now();
        // Pass the current resume data to ensure the latest template is used
        const { url } = await printResume({ id, resumeData: currentResumeData });
        const elapsedTime = Date.now() - startTime;
        const minimumDisplayTime = 2000; // 2 seconds minimum

        if (elapsedTime < minimumDisplayTime) {
          await new Promise((resolve) => setTimeout(resolve, minimumDisplayTime - elapsedTime));
        }

        // Force download the PDF instead of opening in new tab
        await forceDownload(url, `${title}-resume.pdf`);
        setShowPdfLoadingModal(false);
        await queryClient.invalidateQueries({ queryKey: ["user"] });

        // GTM tracking for PDF download
        trackDownloadResume({
          resumeId: id,
          format: "pdf",
          templateName: currentResumeData.metadata.template,
          outcome: "success",
        });

        // Show share modal after a brief delay (let loading modal exit first)
        setTimeout(() => setShowShareModal(true), 400);
      } catch (error: unknown) {
        setShowPdfLoadingModal(false);
        const status =
          typeof error === "object" && error !== null && "response" in error
            ? (error as { response?: { status?: number } }).response?.status
            : undefined;

        if (status === 403) {
          trackDownloadResume({
            resumeId: id,
            format: "pdf",
            templateName,
            outcome: "blocked_free_limit",
          });
          toast({
            variant: "error",
            title: t`Monthly free PDF already used`,
            description: t`You have already used your free PDF export this month.`,
          });
          navigate("/pricing");
          return;
        }

        toast({
          variant: "error",
          title: t`Error`,
          description: t`Failed to generate PDF. Please try again.`,
        });
      }
    }, 100); // Small delay to ensure modal renders
  };

  const onCloseModal = () => {
    setShowAuthModal(false);
  };

  return (
    <>
      <div className="space-y-4">

        {/* Header */}
        <div className="flex items-center gap-3 mb-6 border-b border-gray-200 pb-5">
          <div className="flex size-10 items-center justify-center rounded-xl bg-gray-100">
            <Export size={20} className="text-gray-600" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#16344c]">{t`Export`}</h3>
            <p className="text-[11px] text-gray-400">{t`Download & share`}</p>
          </div>
          <div className="ml-auto">
            <Tooltip content={t`Learn more about exporting`}>
              <Button size="icon" variant="ghost" className="h-8 w-8 text-gray-400 hover:bg-gray-100 hover:text-[#16344c] transition-colors">
                <Question size={18} />
              </Button>
            </Tooltip>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-gray-200 p-5 space-y-6">
          <div className="space-y-4">
          <span className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Formats</span>

          {/* PDF Card */}
          <button
            onClick={onPdfExport}
            className={cn(
              "w-full text-left p-4 rounded-xl border flex gap-4 transition-all group relative overflow-hidden",
              loading && "pointer-events-none opacity-75",
              canDownloadPdf
                ? "bg-white border-gray-200 hover:border-[#268599]/50 hover:bg-[#268599]/5"
                : "bg-orange-500/5 border-orange-500/20 opacity-80 cursor-not-allowed"
            )}
          >
            <div className={cn(
              "w-12 h-12 rounded-lg flex items-center justify-center shrink-0 transition-colors relative",
              canDownloadPdf
                ? "bg-[#268599]/10 text-[#268599] group-hover:bg-[#268599] group-hover:text-white"
                : "bg-orange-500/20 text-orange-400"
            )}>
              {loading ? (
                <CircleNotch size={24} weight="bold" className="animate-spin" />
              ) : (
                <FilePdf size={24} weight="fill" />
              )}

              {!canDownloadPdf && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center border border-orange-500/30 shadow-sm">
                  <Crown size={10} weight="fill" className="text-orange-400" />
                </div>
              )}
            </div>

            <div className="flex-1 flex flex-col justify-center">
              <div className="flex justify-between items-center mb-0.5">
                <span className="text-sm font-bold text-[#16344c] group-hover:text-[#268599] transition-colors">PDF Document</span>
                {!canDownloadPdf && (
                  <span className="text-[9px] uppercase font-bold text-orange-600 bg-orange-500/10 px-1.5 py-0.5 rounded border border-orange-500/30 tracking-wider">
                    {hasUsedMonthlyFreePdfExport ? t`Limit reached` : t`Premium`}
                  </span>
                )}
              </div>
              <span className="text-[10px] text-gray-500 leading-tight">
                {canDownloadPdf
                  ? t`High quality print-ready document. Perfect for email and ATS systems.`
                  : hasUsedMonthlyFreePdfExport
                    ? t`You have already used your free PDF export this month.`
                  : isPremiumTemplate
                    ? t`This template requires a paid subscription.`
                    : t`Upgrade to premium to download as a PDF.`}
              </span>
            </div>

            {canDownloadPdf && (
              <div className="absolute inset-x-0 bottom-0 h-0.5 bg-[#268599] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300 opacity-50" />
            )}
          </button>

          {/* JSON Card */}
          <button
            onClick={onJsonExport}
            className="w-full text-left p-4 rounded-xl border border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50 flex gap-4 transition-all group relative overflow-hidden"
          >
            <div className="w-12 h-12 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center shrink-0 group-hover:bg-gray-200 group-hover:text-[#16344c] transition-colors">
              <FileCode size={24} weight="fill" />
            </div>

            <div className="flex-1 flex flex-col justify-center">
              <span className="text-sm font-bold text-[#16344c] mb-0.5">JSON Data Backup</span>
              <span className="text-[10px] text-gray-500 leading-tight">
                Raw data snapshot. Used to import or backup your styling setup.
              </span>
            </div>
          </button>
        </div>

        {isVerified && <hr className="border-gray-200 my-6" />}

        {/* Intelli Verified Badge Toggle */}
        {isVerified && (
          <div className="space-y-3">
            <span className="text-[10px] uppercase font-bold text-[#268599]/80 tracking-wider flex items-center gap-1">
              <ShieldCheck size={12} weight="fill" className="text-[#268599]" /> Add-ons
            </span>

            <div className="p-1 rounded-2xl bg-[#09b9aa]/5 border border-[#09b9aa]/20 overflow-hidden relative">
              {/* Glow */}
              <div className="absolute -right-10 -bottom-10 w-32 h-32 bg-[#109be6]/10 blur-[40px] pointer-events-none"></div>

              <label className="flex items-center justify-between p-3 rounded-xl hover:bg-[#09b9aa]/10 transition-all cursor-pointer group relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#109be6] to-[#09b9aa] flex items-center justify-center shadow-[0_0_15px_rgba(9,185,170,0.3)]">
                    <ShieldCheck size={20} weight="fill" className="text-white" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-sm font-semibold text-[#16344c]">{t`Verified Badge`}</span>
                    <span className="text-[10px] text-gray-500">{t`Display Intelli Verified mark on PDF`}</span>
                  </div>
                </div>

                {/* Custom Premium Switch */}
                <div className="relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full pointer-events-none">
                  <input
                    type="checkbox"
                    className="peer sr-only"
                    checked={showVerifiedBadge}
                    onChange={(e) => setValue("metadata.showVerifiedBadge", e.target.checked)}
                  />
                  <div className="h-full w-full rounded-full bg-gray-200 border border-gray-300 transition-all peer-checked:bg-[#09b9aa]/40 peer-checked:border-[#09b9aa]/50"></div>
                  <div className="group pointer-events-none absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-all peer-checked:translate-x-5 peer-checked:bg-[#109be6] shadow-[0_0_10px_rgba(20,184,166,0)] peer-checked:shadow-[0_0_10px_rgba(16,155,230,0.5)]"></div>
                </div>
              </label>
            </div>
          </div>
        )}

        </div>{/* end white card */}

      </div>

      {/* Signup Prompt Modal */}
      <SignupModal
        isOpen={showAuthModal}
        transferGuestResume
        prefilledEmail={userData.email}
        onClose={onCloseModal}
      />

      {/* PDF Loading Modal */}
      <PdfLoadingModal isOpen={showPdfLoadingModal} />

      {/* Post-Download Share Modal */}
      <PostDownloadShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
};
